// src/components/BusinessCRMDash.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./businessCRMDash.css";
import { supabase } from "../../supabase_client.jsx";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from "recharts";

export default function BusinessCRMDash({ currentUser, onViewSelect, onProfileSetupComplete }) {
  const handleProfileSetupComplete = () => {
    setIsProfileComplete(true);
    if (onProfileSetupComplete) onProfileSetupComplete();
  };

  const userId = currentUser?.user?.id;

  const [username, setUsername] = useState("");
  const [refresh, setRefresh] = useState(0);

  const [kpis, setKpis] = useState({
    totalEvents: 0,
    upcomingEvents: 0,
    totalRSVPs: 0,
    totalBookmarks: 0,
    avgSignups: 0,
    medianAge: 0,
  });

  const [animated, setAnimated] = useState(kpis);
  const [genderData, setGenderData] = useState([]);
  const [topEvent, setTopEvent] = useState(null);
  const [leastEvent, setLeastEvent] = useState(null);

  const [extraStats, setExtraStats] = useState({
    malePercent: 0,
    femalePercent: 0,
    topCategory: "N/A",
    repeatAttendees: 0,
    checkinRate: 0,
  });

  const initials = useMemo(() => {
    if (!username) return "B";
    return username.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  }, [username]);

  const [logoUrl, setLogoUrl] = useState("");
  const [isProfileComplete, setIsProfileComplete] = useState(true);

  // Auto refresh
  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      // Get username
      const { data: userData } = await supabase
        .from("users")
        .select("username")
        .eq("user_id", userId)
        .single();

      const fetchedUsername = userData?.username;
      if (!fetchedUsername) return;
      setUsername(fetchedUsername);

      // Get logo
      const { data: bizData } = await supabase
        .from("businesses")
        .select("logo_url, has_setup_profile")
        .eq("user_id", userId)
        .single();

      setLogoUrl(bizData?.logo_url || "");
      setIsProfileComplete(bizData.has_setup_profile === true); 

      // Get events
      const { data: eventData } = await supabase
        .from("events")
        .select("*")
        .eq("event_owner", fetchedUsername);

      if (!eventData) return;

      const today = new Date();
      const upcoming = eventData.filter(
        (event) => new Date(event.date) >= today
      );

      const eventIds = eventData.map((e) => e.id);

      // RSVPs
      const { data: rsvpData } = await supabase
        .from("marked_yes_events")
        .select("user_id, event_id")
        .in("event_id", eventIds);

      // Bookmarks
      const { data: bookmarkData } = await supabase
        .from("bookmarked_events")
        .select("event_id")
        .in("event_id", eventIds);

      const totalRSVPs = rsvpData?.length || 0;
      const totalBookmarks = bookmarkData?.length || 0;

      // Event performance
      const eventCounts = {};
      rsvpData?.forEach((r) => {
        eventCounts[r.event_id] =
          (eventCounts[r.event_id] || 0) + 1;
      });

      let most = null;
      let least = null;

      eventData.forEach((event) => {
        const count = eventCounts[event.id] || 0;
        if (!most || count > most.count)
          most = { name: event.name, count };
        if (!least || count < least.count)
          least = { name: event.name, count };
      });

      setTopEvent(most);
      setLeastEvent(least);

      const avgSignups =
        eventData.length > 0
          ? Math.round(totalRSVPs / eventData.length)
          : 0;

      // Unique attendee IDs
      const attendeeIds = [
        ...new Set(rsvpData?.map((r) => r.user_id))
      ];

      // 🔥 FIX: Proper join through users → profile_meta
      const { data: profiles } = await supabase
        .from("users")
        .select(`
          user_id,
          user_profile_meta (
            age,
            gender
          )
        `)
        .in("user_id", attendeeIds);

      // Flatten joined data
      const flatProfiles = profiles
        ?.map((p) => p.user_profile_meta)
        .filter(Boolean) || [];

      // Median Age
      const ages = flatProfiles
        .map((p) => Number(p.age))
        .filter((age) => !isNaN(age));

      ages.sort((a, b) => a - b);

      let medianAge = 0;
      if (ages.length) {
        const mid = Math.floor(ages.length / 2);
        medianAge =
          ages.length % 2 !== 0
            ? ages[mid]
            : Math.round((ages[mid - 1] + ages[mid]) / 2);
      }

      // Gender counts
      const genderCounts = {};
      flatProfiles.forEach((p) => {
        if (!p.gender) return;
        const g = p.gender.toLowerCase();
        genderCounts[g] = (genderCounts[g] || 0) + 1;
      });

      const genderChart = Object.keys(genderCounts).map((key) => ({
        name: key,
        value: genderCounts[key],
      }));

      setGenderData(genderChart);

      // Gender %
      const totalGender = Object.values(genderCounts).reduce((a, b) => a + b, 0);

      const malePercent = totalGender
        ? Math.round(((genderCounts["male"] || 0) / totalGender) * 100)
        : 0;

      const femalePercent = totalGender
        ? Math.round(((genderCounts["female"] || 0) / totalGender) * 100)
        : 0;

      // Category stats
      const { data: categoryData } = await supabase
        .from("event_category")
        .select("event_id, category")
        .in("event_id", eventIds);

      const categoryCounts = {};
      categoryData?.forEach((c) => {
        categoryCounts[c.category] =
          (categoryCounts[c.category] || 0) + 1;
      });

      let topCategory = "N/A";
      let max = 0;

      Object.entries(categoryCounts).forEach(([cat, count]) => {
        if (count > max) {
          max = count;
          topCategory = cat;
        }
      });

      // Repeat attendees
      const attendeeFrequency = {};
      rsvpData?.forEach((r) => {
        attendeeFrequency[r.user_id] =
          (attendeeFrequency[r.user_id] || 0) + 1;
      });

      const repeatAttendees = Object.values(attendeeFrequency).filter(
        (count) => count > 1
      ).length;

      // Check-in rate
      const { data: checkinData } = await supabase
        .from("checkins")
        .select("event_id")
        .in("event_id", eventIds);

      const totalCheckins = checkinData?.length || 0;

      const checkinRate =
        totalRSVPs > 0
          ? Math.round((totalCheckins / totalRSVPs) * 100)
          : 0;

      setExtraStats({
        malePercent,
        femalePercent,
        topCategory,
        repeatAttendees,
        checkinRate,
      });

      setKpis({
        totalEvents: eventData.length,
        upcomingEvents: upcoming.length,
        totalRSVPs,
        totalBookmarks,
        avgSignups,
        medianAge,
      });
    };

    fetchData();
  }, [userId, refresh]);

  useEffect(() => {
    const duration = 800;
    const steps = 30;
    const interval = duration / steps;

    Object.keys(kpis).forEach((key) => {
      let start = 0;
      const end = kpis[key];
      const increment = end / steps;

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          start = end;
          clearInterval(timer);
        }

        setAnimated((prev) => ({
          ...prev,
          [key]: Math.floor(start),
        }));
      }, interval);
    });
  }, [kpis]);

  const COLORS = ["#6ce5e8", "#e2cbf6"];

  return (
    <div className="home-container">
      <div className="home-card">

      <div className="app-title-banner">
            <h1 className="app-title-text">Do You Wanna Go?</h1>
      </div>

        {/* header */}
        <div className="calendar-top-nav">
          <div className="nav-left">
            <button className="nav-tab" onClick={() => onViewSelect('businessHome')}>General</button>
            <button className="nav-tab" onClick={() => onViewSelect('business')}>Calendar</button>
            <button className="nav-tab active">Dashboard</button>
            <button className="nav-tab" onClick={() => onViewSelect('event-management')}>Event Management</button>
          </div>
          <div className="nav-right">
            <div className="nav-avatar-circle" style={{ position: "relative" }} onClick={() => onViewSelect("business-profile")}>
              {logoUrl
                ? <img src={logoUrl} alt="profile" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                : initials
              }
              {!isProfileComplete && <span className="avatar-dot" />}
            </div>
          </div>
        </div>

        <div className="dashboard-container">
          <div className="dash-header">
            <h1>{username}'s Analytics Dashboard</h1>
          </div>

          <section className="kpi-grid">
            <div className="kpi-card"><h3>{animated.totalEvents}</h3><p>Total Events</p></div>
            <div className="kpi-card"><h3>{animated.upcomingEvents}</h3><p>Upcoming Events</p></div>
            <div className="kpi-card"><h3>{animated.totalRSVPs}</h3><p>Total RSVPs</p></div>
            <div className="kpi-card"><h3>{animated.totalBookmarks}</h3><p>Total Bookmarks</p></div>
            <div className="kpi-card"><h3>{animated.avgSignups}</h3><p>Avg Signups / Event</p></div>
          </section>

          <section className="analytics-grid">
            <div className="analytics-card">
              <h3>Top Performing Event</h3>
              <p>{topEvent ? `${topEvent.name} (${topEvent.count} signups)` : "N/A"}</p>
            </div>

            <div className="analytics-card">
              <h3>Lowest Performing Event</h3>
              <p>{leastEvent ? `${leastEvent.name} (${leastEvent.count} signups)` : "N/A"}</p>
            </div>

            <div className="analytics-card">
              <h3>Most Popular Category</h3>
              <p>{extraStats.topCategory}</p>
            </div>

            <div className="analytics-card">
              <h3>Repeat Attendees</h3>
              <p>{extraStats.repeatAttendees} users</p>
            </div>

            <div className="analytics-card">
              <h3>Check-in Rate</h3>
              <p>{extraStats.checkinRate}%</p>
            </div>

            <div style={{ width: "100%", height: 400 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={genderData} cx="50%" cy="50%" outerRadius={120} dataKey="value">
                    {genderData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

          </section>
        </div>
      </div>
    </div>
  );
}